"use client";
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Reveal from './Reveal';
import { useTranslations } from 'next-intl';

const RoadmapItem = ({ item, index, isActiveLogo, onRefUpdate }:any) => {
    const itemRef = useRef<HTMLDivElement>(null);
    const [isActive, setIsActive] = useState(false);

    useEffect(() => {
        onRefUpdate(index, itemRef.current);
    }, [index, onRefUpdate]);

    useEffect(() => {
        const handleScroll = () => {
            if (!itemRef.current) return;

            const rect = itemRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const halfScreen = windowHeight / 2;
            
            const elementTop = rect.top;
            const elementHeight = rect.height;
            
            const isInTopHalf = elementTop < halfScreen && (elementTop + elementHeight) > 0;
            
            setIsActive(isInTopHalf);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll();
        
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div ref={itemRef} className="flex items-start justify-between w-[100%] min-h-[316px]">
            <div className="w-[45%] flex justify-center max-sm:hidden">
                    {/* <Image
                        src="/images/icons/logo-network-bigger.png"
                        alt="Logo Hyra Network"
                        width={300}
                        height={200}
                        className={`z-10 max-h-[316px] transition-opacity duration-1000 ${isActiveLogo? 'opacity-100':'opacity-0'}`}
                    /> */}
            </div>
        
            <div className='bg-white z-10'>
                <Image
                    src="/images/icons/logo-network.svg"
                    alt="dot"
                    width={24}
                    height={24}
                    className={`${
                        isActive ? 'opacity-100' : 'opacity-10'
                    }`}
                />
            </div>
            
            <div className={`w-[45%] max-sm:w-[90%] transition-opacity duration-500 ${
                        isActive ? 'opacity-100' : 'opacity-30'
                    }`}>
                <p className='font-open-sans text-base text-[#06f] font-normal uppercase'>{item.year}</p>
                <h3 className="mt-10 text-[24px]/[32px]">{item.title}</h3>
                <p className='mt-3 whitespace-pre-line text-sm font-light text-[#646464]'>{item.description}</p>
            </div>
        </div>
    );
};

export default function Roadmap() {

    const sectionRef = useRef<HTMLDivElement>(null);
    const [activeItem, setActiveItem] = useState<number | null>(0);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const t = useTranslations('enterprise');
    const roadmapData = [
        {
            year: 2025,
            title: t('network-genesis-phase'),
            description: t('phase1'),
        },
        {
            year: 2026,
            title: t('decentralization-phase'),
            description: t('phase2'),
        },
        {
            year: 2027,
            title: t('protocol-maturation-phase'),
            description: t('phase3'),
        },
        {
            year: 2028,
            title: t('global-expansion-phase'),
            description: t('phase4'),
        },
        {
            year: 2029,
            title: t('ipo-preparation-phase'),
            description: t('phase5'),
        },
        {
            year: 2030,
            title: t('public-listing-and-global-dao-governance'),
            description: t('phase6'),
        },
    ]
    
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start end", "end start"]
    });

    const rawHeight = useTransform(scrollYProgress, () => {
        if (!sectionRef.current) return '0%';

        const rect = sectionRef.current.getBoundingClientRect();
        const winH = window.innerHeight;
        const half   = winH / 2;

        const topVisible = Math.max(0, half - rect.top);
        const maxH       = Math.min(rect.height, topVisible);
        const percent    = (maxH / rect.height) * 100;

        return `${Math.max(0, percent)}%`;
    });
    /* trả lại số cho spring xử lý */
    const numericHeight = useTransform(rawHeight, v => +v.replace('%', ''));

    /*  quán tính */
    const smoothNumeric = useSpring(numericHeight, {
        stiffness: 200, 
        damping: 24,  
        mass: 0.8
    });

    /*  Biến lại thành chuỗi % cho CSS */
    const smoothHeight = useTransform(smoothNumeric, v => `${v}%`);
    const barPx = useTransform(smoothNumeric, v => {
        if (!sectionRef.current) return 0;

        const sectionH = sectionRef.current.getBoundingClientRect().height * 0.95; // 95 % bạn set ở h-[95%]
        const y = (v / 100) * sectionH;

        /* 2. kẹp tối đa — logo dừng khi thanh hết */
        return Math.min(y, sectionH); // 200 px ≈ chiều cao logo
    });
    const logoY = useTransform(barPx, y =>
        y <= 150 ? 0         
                : y - 150
    );
    
    // const progressHeight = useTransform(scrollYProgress, (value) => {
    //     if (!sectionRef.current) return "0%";
        
    //     const rect = sectionRef.current.getBoundingClientRect();
    //     const windowHeight = window.innerHeight;
    //     const halfScreen = windowHeight / 2;
        
    //     // Phần của section nằm trên 50% màn hình
    //     const topVisible = Math.max(0, halfScreen - rect.top);
    //     const maxHeight = Math.min(rect.height, topVisible);
        
    //     // Convert sang percentage
    //     const percentage = (maxHeight / rect.height) * 100;
    //     return `${Math.max(0, percentage)}%`;
    // });

    useEffect(() => {
        const handleScroll = () => {
            const windowHeight = window.innerHeight;
            const centerY = windowHeight / 2;
            let closestIndex = null;
            let closestDistance = Infinity;

            itemRefs.current.forEach((ref, index) => {
                if (!ref) return;
                
                const rect = ref.getBoundingClientRect();
                const itemCenterY = rect.top + rect.height / 2;
                const distance = Math.abs(itemCenterY - centerY);
                
                // Chỉ xét items đang visible và gần center
                if (rect.top < windowHeight && rect.bottom > 0 && distance < closestDistance) {
                    closestDistance = distance;
                    closestIndex = index;
                }
            });

            setActiveItem(closestDistance < 200 ? closestIndex : 0);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        handleScroll(); // Initial check
        
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Update RoadmapItem to use ref
    const updateItemRef = (index: number, ref: HTMLDivElement | null) => {
        itemRefs.current[index] = ref;
    };

    return (
        <section className="py-20 px-[78px] max-lg:px-10 max-sm:px-4" id="roadmap">
            <div className="flex flex-col justify-center items-center text-center">
                <p className="font-open-sans text-base text-[#06f] font-normal uppercase">{t('6-key-phases')}</p>
                <Reveal 
                    delay={0.1} 
                    duration={0.8} 
                    direction="left"
                    withBlur={true}
                    blurAmount={8}
                >
                    <h2 className="text-center text-[48px]/[56px] max-sm:text-2xl tracking-[-1px] mt-12">{t('roadmap')}</h2>
                </Reveal>
                <Reveal 
                    delay={0.5} 
                    duration={0.8} 
                    direction="left"
                    withBlur={true}
                    blurAmount={8}
                >
                    <p className="mt-6 text-base/[24px] max-sm:text-sm font-light text-[#646464] max-w-[661px]"> {t('six-phase-roadmap-from-2025-to-2030-genesis-decentralization-maturation-expansion-ipo-preparation-and-global-dao-governance')}</p>
                </Reveal>
            </div>
            <div className="relative overflow-hidden mt-[56px] max-w-[1440px] mx-auto">
                <div ref={sectionRef}  className="absolute h-[95%] translate-y-4 left-1/2 max-sm:left-3 -translate-x-1/2">
                    <div className="w-1 h-full bg-[#06f] opacity-10"></div>             
                    <motion.div
                        className="absolute top-0 left-0 w-1 bg-[#06f] opacity-100"
                        style={{ height: smoothHeight }}
                    >
                    </motion.div>

                    {/* logo trượt */}
                    <motion.div
                        className="absolute -left-[400px] max-lg:-left-[350px] max-md:-left-[300px] max-sm:hidden top-0"
                        style={{ translateY: logoY }}
                    >
                        <Image src="/images/icons/new-logo.svg" alt="Hyra Logo" width={250} height={200} className="max-h-[316px]"/>
                    </motion.div>
                </div>
                {roadmapData.map((item, index) => (
                    <RoadmapItem 
                        key={index}
                        item={item}
                        index={index}
                        isActiveLogo={activeItem === index}
                        onRefUpdate={updateItemRef}
                    />
                ))}
            </div>
        </section>
    )
}


